import CustomField from 'common/components/CustomField'
import PasswordStrengthMeter from 'common/components/PasswordStrengthMeter'
import {Form, Formik, FormikHelpers, FormikValues} from 'formik'
import {FC} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import {Link, useNavigate} from 'react-router'
import {toast} from 'react-toastify'
import {RootState} from 'setup'
import {REDUX_SET_ACCESS_TOKEN} from 'setup/redux/Actions/SignIn/ActionTypes/ReduxActionTypes'
import {resetDefaultPassword} from 'setup/redux/Actions/SignIn/forgotPassword'
import * as Yup from 'yup'

import {
  clearMustCreatePassword,
  isPasswordAlreadySetError,
} from '../selective/mustCreatePasswordHelpers'

type Props = {
  /** Called once the new password is saved (clears the must-change flag). */
  onSuccess: () => void
}

const MIN_LENGTH = 8
const MAX_LENGTH = 20

/**
 * Forced first-login password change for classic tenants (random password
 * provisioning). Rendered inside AuthLayout so it carries the school's logo,
 * background and the same Metronic form styling as the other auth pages; the
 * selective-AI product keeps its own CreatePassword screen.
 *
 * Posts to school/user/reset-default-password, which returns a fresh access
 * token (the generated-password token is invalidated server-side).
 */
const ChangeGeneratedPassword: FC<Props> = ({onSuccess}) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const loggedInUser = useSelector<RootState>((state) => state.userReducer.user) as any
  const accountUsername = loggedInUser?.email || loggedInUser?.username || ''

  const schema = Yup.object().shape({
    password: Yup.string()
      .label(intl.formatMessage({id: 'GENERAL.NEW_PASSWORD'}))
      .min(MIN_LENGTH, intl.formatMessage({id: 'GENERAL.PASSWORD_MIN_LENGTH'}))
      .max(MAX_LENGTH)
      .matches(/[A-Z]/, intl.formatMessage({id: 'GENERAL.PASSWORD_UPPERCASE'}))
      .matches(/\d/, intl.formatMessage({id: 'GENERAL.PASSWORD_NUMBER'}))
      .matches(/[^A-Za-z0-9]/, intl.formatMessage({id: 'GENERAL.PASSWORD_SPECIAL'}))
      .required(),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password')], intl.formatMessage({id: 'GENERAL.PASSWORDS_MUST_MATCH'}))
      .label(intl.formatMessage({id: 'GENERAL.CONFIRM_PASSWORD'}))
      .required(),
  })

  const submit = (values: FormikValues, actions: FormikHelpers<any>) => {
    actions.setSubmitting(true)
    actions.setStatus('')

    const finish = () => {
      onSuccess()
      navigate('/dashboard', {replace: true})
    }

    dispatch(
      resetDefaultPassword(
        values.password,
        (response: any) => {
          // Swap in the fresh token: the generated-password token was just
          // invalidated by the token-version bump. Redux-persist keeps it.
          const newAccessToken = response?.result?.accessToken
          if (newAccessToken) {
            dispatch({type: REDUX_SET_ACCESS_TOKEN, payload: newAccessToken})
          }
          actions.setSubmitting(false)
          toast.success(intl.formatMessage({id: 'PROFILE.PASSWORD_CHANGED_SUCCESSFULLY'}))
          finish()
        },
        (res: any) => {
          actions.setSubmitting(false)
          if (isPasswordAlreadySetError(res)) {
            clearMustCreatePassword(dispatch)
            finish()
            return
          }
          actions.setStatus(
            res?.message ?? intl.formatMessage({id: 'GENERAL.SOMETHING_WENT_WRONG'})
          )
        }
      )
    )
  }

  return (
    <Formik
      validationSchema={schema}
      initialValues={{password: '', confirmPassword: ''}}
      onSubmit={submit}
      validateOnMount
    >
      {({status, isSubmitting, values, isValid}) => (
        <Form className='form w-100' noValidate data-testid='change-generated-password-form'>
          <div className='text-center mb-10'>
            <h1 className='text-dark fw-bolder mb-3'>
              {intl.formatMessage({id: 'AUTH.CREATE_PASSWORD.FORCED_TITLE'})}
            </h1>
            <div className='text-gray-500 fw-semibold fs-6'>
              {intl.formatMessage({id: 'AUTH.CREATE_PASSWORD.FORCED_DESC'})}
            </div>
          </div>

          {status && (
            <div className='mb-8 alert alert-danger'>
              <div className='alert-text fw-bold'>{status}</div>
            </div>
          )}

          {/* Anchors the saved credential to this account for password managers. */}
          {accountUsername && (
            <input type='hidden' name='username' autoComplete='username' value={accountUsername} />
          )}

          <CustomField
            type='password'
            name='password'
            label='GENERAL.NEW_PASSWORD'
            fieldClassName='form-control-lg form-control-solid'
            required
            autoComplete='new-password'
          />

          <PasswordStrengthMeter password={values.password} />

          <CustomField
            type='password'
            name='confirmPassword'
            label='GENERAL.CONFIRM_PASSWORD'
            fieldClassName='form-control-lg form-control-solid'
            required
            autoComplete='new-password'
          />

          <div className='d-grid mt-8'>
            <button
              type='submit'
              className='btn btn-primary btn-lg'
              disabled={isSubmitting || !isValid}
              id='kt_change_generated_password_submit'
            >
              {isSubmitting && (
                <span
                  className='spinner-border spinner-border-sm me-2'
                  role='status'
                  aria-hidden='true'
                />
              )}
              {intl.formatMessage({id: 'GENERAL.CONTINUE'})}
            </button>
          </div>

          <div className='text-center mt-6'>
            <Link
              to='/logout'
              className='link-primary fw-semibold'
              data-testid='create-password-sign-out'
            >
              {intl.formatMessage({id: 'MENU.SIGN_OUT'})}
            </Link>
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default ChangeGeneratedPassword
